import React, { useState } from 'react'
import moment from 'moment'
import { Drawer } from 'antd'
import Select from 'react-select'
import { useHistory } from 'react-router-dom'
import { useSnackbar } from 'notistack'

import { normalize, notifOps, summarizeProcurementInvoice, useListData, usePet, useToggle } from '../../modules'

import Datepicker from '../reusable/datePicker'
import SelectLabel from '../reusable/selectLabel'
import Confirm from '../reusable/confirm'

const RecordPayment = ({ invoices = [], supplier, onPerform }) => {
    const invoicesTotal = invoices.reduce((total, inv) => total + summarizeProcurementInvoice(inv?.items, inv?.amountPaid, inv?.amountCredited).outstanding, 0)
    
    const [date, setDate] = useState(moment())
    const [amount, setAmount] = useState(invoicesTotal)
    const [charges, setCharges] = useState(0)
    const [notes, setNotes] = useState()
    const [account, setAccount] = useState()
    const [method, setMethod] = useState()
    const [ref, setRef] = useState()

    const [accounts, setAccounts] = useState([])
    const [methods, setMethods] = useState([])

    const { enqueueSnackbar } = useSnackbar()

    const [visible, toggleVisible] = useToggle(true)

    const history = useHistory()

    const [
        { loading: accLoading, error },
        { navigate, Paginate, page = 1, limit = 10 }
    ] = useListData('accounts', { string: '' }, { notify: true, manual: false }, 'accounts', 100, ({ accounts: data }) => {
        let obj = {}

        data.forEach(acc => {
            if (!obj[acc.type])
                obj[acc.type] = []

            obj[acc.type].push({
                label: acc.name,
                value: acc._id
            })
        })

        let final = []
        for (let typ in obj) {
            final.push({
                label: typ,
                options: obj[typ]
            })
        }

        setAccounts(final)
    })

    const modify = e => {
        switch (e.target.name) {
            case 'amount':
                if (Number(e.target.value) > invoicesTotal) {
                    enqueueSnackbar(`Payment amount cannot be higher than invoices total (Ksh. ${normalize(invoicesTotal)})`, notifOps('warning'))
                    return 
                }

                setAmount(e.target.value)
                break

            case 'charges':
                setCharges(e.target.value)
                break

            case 'notes':
                setNotes(e.target.value)
                break

            case 'ref':
                setRef(e.target.value)
                break

            default:
                break
        }
    }

    const enableAmount = amount > 0 && amount <= invoicesTotal
    const enableCharges = charges >= 0
    const enableNotes = notes?.length > 0

    usePet('paymentMethodNames', { type: 'regular' }, { notify: true, callback: ({ data }) => setMethods(data) })

    const close = () => {
        toggleVisible()
        // onClose()
        setTimeout(() => history.goBack(), 300)
    }

    const onComplete = ({ data }, notif, ops) => {
        onPerform()
        notif('Payment has been recorded', ops('success'))
        
        setTimeout(close, 300)
    }

    const [{ loading }, execute] = usePet('payInvoices', {
        date, 
        payments: [
            {
                method: method?.value,
                account: account?.value, 
                amount,
                charges,
                ref,
                notes
            }
        ],
        invoices: invoices.map(inv => inv._id),
        subjectType: 'Supplier', 
        subject: supplier?._id, 
        subjectName: supplier?.name
    }, { notify: true, callback: onComplete, manual: true })

    const recordPayment = () => {
        if (!method)
            enqueueSnackbar('Please indicate the payment method', notifOps('warning'))

        if (!account)
            enqueueSnackbar('Please indicate the payment account', notifOps('warning'))

        if (!enableAmount)
            enqueueSnackbar('Please enter the correct format for amount', notifOps('warning'))

        if (!enableCharges)
            enqueueSnackbar('Please enter the correct format for charges', notifOps('warning'))

        if (!enableNotes)
            enqueueSnackbar('Please enter the correct format for notes', notifOps('warning'))

        if (method && account && enableAmount && enableCharges && enableNotes)
            Confirm({
                title: `Record payment of Ksh. ${amount}?`,
                message: `This will deduct Ksh. ${amount} from ${account?.label} in favour of ${supplier?.name}`,
                okText: 'Record Payment',
                onOk: execute
            })
    }

    return (
        <Drawer
            closable
            maskClosable
            visible={visible}
            onClose={close}
            footer={
                <div className="w-full text-right">
                    <button onClick={ recordPayment } className="text-white mr-2 hisani-btn-orange font-medium rounded-lg text-sm px-5 py-2.5 text-center">Save</button>
                </div>
            }
            width="35%">
            <p className="font-extrabold text-3xl mb-0">Record Payment</p>
            <p className="text-sm italic mb-0 text-gray-700">Total selected amount: <span className="font-semibold">Ksh. { normalize(invoicesTotal) }</span> </p>
            <div className="flex flex-wrap mt-3">
                <div className="w-full px-3 mb-1">
                    <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2" htmlFor="grid-name">
                        Date
                    </label>
                    <Datepicker value={date} onChange={setDate} />
                </div>
                <div className="w-full px-3 mb-1">
                    <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2" htmlFor="grid-username">
                        Paid Through
                    </label>
                    <Select
                        className="mb-3"
                        value={account}
                        onChange={setAccount}
                        formatGroupLabel={ SelectLabel }
                        options={accounts}
                        placeholder="Select account" />
                </div>
                <div className="w-full px-3 mb-1">
                    <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2" htmlFor="grid-username">
                        Payment Method
                    </label>
                    <Select
                        className="mb-3"
                        value={method}
                        onChange={setMethod}
                        options={methods.map(method => ({ label: method, value: method, amount: 0 }))}
                        placeholder="Select payment methods" />
                </div>
                <div className="w-full px-3 mb-1">
                    <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2" htmlFor="grid-name">
                        Amount Paid
                    </label>
                    <input
                        className="appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
                        id="grid-amount"
                        type="number"
                        name="amount"
                        value={amount}
                        onChange={modify}
                        placeholder="2000" />
                </div>
                <div className="w-full px-3 mb-1">
                    <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2" htmlFor="grid-name">
                        Transaction Charges <span className="text-gray-500 text-xs normal-case italic">(If Any)</span>
                    </label>
                    <input
                        className="appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
                        id="grid-charges"
                        type="number"
                        name="charges"
                        value={charges}
                        onChange={modify}
                        placeholder="27" />
                    <p className="text-gray-500 text-xs italic">Indicate any transaction charges here</p>
                </div>
                <div className="w-full px-3 mb-1">
                    <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2" htmlFor="grid-name">
                        Transaction Ref <span className="text-gray-500 text-xs normal-case italic">(Optional)</span>
                    </label>
                    <input
                        className="appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
                        id="grid-ref"
                        type="text"
                        name="ref"
                        value={ref}
                        onChange={modify}
                        placeholder="XXXXXX" />
                    <p className="text-gray-500 text-xs italic">Indicate any transaction codes, cheque numbers, etc</p>
                </div>
                <div className="w-full px-3 mb-1">
                    <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2" htmlFor="grid-name">
                        Notes
                    </label>
                    <textarea
                        rows={ 4 }
                        className="appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
                        id="grid-notes"
                        name="notes"
                        value={notes}
                        onChange={modify}
                        placeholder="Notes..." />
                </div>
            </div>
        </Drawer>
    )
}

export default RecordPayment
